import { Button, Modal, Spinner } from 'react-bootstrap';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';

export interface ConfirmDialogProps {
  show: boolean;
  title?: string | ( () => string | React.JSX.Element );
  okText?: string;
  cancelText?: string;
  onCancel?: () => Promise<unknown> | unknown;
  onConfirm: () => Promise<unknown> | unknown;
}

/**
 * Simple confirmation dialog component. Put the body of the dialog in the `children` prop.
 * Pass `onCancel` and `onConfirm` callbacks, and one of which will be called depending on
 * the user's choice. The dialog will render a spinner for the duration of the callbacks,
 * so you can perform long-running operations and the user will know something is going
 * on.
 */
export function ConfirmDialog( {
  show,
  onCancel,
  onConfirm,
  title,
  okText,
  cancelText,
  children,
}: PropsWithChildren<ConfirmDialogProps> ) {
  const ok = okText ?? 'OK';
  const cancel = cancelText ?? 'Cancel';

  const [working, setWorking] = useState( false );

  useEffect( () => {
    // reset 'working' to false when the dialog is closed
    if ( !show ) {
      setWorking( false );
    }
  }, [show] );

  const handleCancel = useCallback( async () => {
    setWorking( true );
    if ( onCancel ) {
      await onCancel();
    } else {
      await onConfirm();
    }
  }, [onCancel, onConfirm] );

  const handleConfirm = useCallback( async () => {
    setWorking( true );
    onConfirm();
  }, [onConfirm] );

  const renderedTitle = typeof title === 'function' ? title() : ( title ?? 'Confirm' );

  if ( !show ) return null;

  return (
    <Modal show={ show } onHide={ handleCancel }>
      <Modal.Header>
        <Modal.Title>{ renderedTitle }</Modal.Title>
      </Modal.Header>
      <Modal.Body>{ children }</Modal.Body>
      <Modal.Footer>
        { onCancel && (
          <Button variant="secondary" onClick={ handleCancel } disabled={ working }>
            { cancel }
          </Button>
        ) }
        <Button variant="primary" onClick={ handleConfirm } disabled={ working }>
          { working && <Spinner animation="border" size="sm" /> }
          { ok }
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
